import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import { useQuery } from '@apollo/react-hooks';

import Tabs from '@material-ui/core/Tabs';
import { Typography, Tab } from '@material-ui/core';

import { STATISTICS } from 'graphql/queries/statistics';
import {
  AppHeader,
  CustomDateRangePicker,
  SelectCompanyBox,
  TabPanel
} from 'components';
import StatisticsInfoTab from './components/statistics-charts-tabs/StatisticsChartsTabs';
import StatisticsBookingsList from './components/statistics-bookings-list/StatisticsBookingsList';
import {
  getPeriodRange,
  prepareReceivedStatisticsData
} from 'utils/statistics/prepareStatisticsData';

import useStyles from './styles';

function verticalTabProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`
  };
}
const initialValues = {
  statisticsPeriod: {
    startDate: moment().subtract(1, 'month'),
    endDate: moment()
  }
};
const ACCOMMODATION_STATISTICS_TAB = 0;
const OPEN_ACTIVITIES_STATISTICS_TAB = 1;
const PRIVATE_ACTIVITIES_STATISTICS_TAB = 2;

const Statistics = props => {
  const classes = useStyles();
  const [t] = useTranslation();
  const { statisticsDate, businessOwner } = props;

  const [value, setValue] = React.useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const statisticsPeriodRange = getPeriodRange(
    statisticsDate.values.statisticsPeriod
  );

  const { data, refetch } = useQuery(STATISTICS, {
    variables: {
      ...statisticsPeriodRange,
      businessOwnerId:
        businessOwner.selectedBusinessOwner.ownerId ||
        businessOwner.selectedBusinessOwner.id
    }
  });
  const statisticsData = prepareReceivedStatisticsData(data);

  React.useEffect(() => {
    refetch();
  }, [statisticsDate]);

  return (
    <div className={classes.root}>
      <AppHeader />
      {!businessOwner.selectedBusinessOwner.id && <SelectCompanyBox />}
      {businessOwner.selectedBusinessOwner.id && (
        <div className={classes.toolbar}>
          <div className={classes.toolbarLeft}>
            <Typography variant="h2" className={classes.toolbarTitle}>
              {t('Statistics')}
            </Typography>
          </div>
          <div className={classes.toolbarSwitchers}>
            <Tabs
              indicatorColor="primary"
              value={value}
              onChange={handleChange}
              orientation="horizontal"
              variant="scrollable"
            >
              <Tab
                classes={{
                  root: classes.customTabRoot,
                  wrapper: classes.customTabWrapper,
                  selected: classes.selected,
                  textColorInherit: classes.textColorInheritCustom
                }}
                label={t('Accommodations')}
                {...verticalTabProps(ACCOMMODATION_STATISTICS_TAB)}
              />
              <Tab
                classes={{
                  root: classes.customTabRoot,
                  wrapper: classes.customTabWrapper,
                  selected: classes.selected,
                  textColorInherit: classes.textColorInheritCustom
                }}
                label={t('Open activities')}
                {...verticalTabProps(OPEN_ACTIVITIES_STATISTICS_TAB)}
              />
              <Tab
                classes={{
                  root: classes.customTabRoot,
                  wrapper: classes.customTabWrapper,
                  selected: classes.selected,
                  textColorInherit: classes.textColorInheritCustom
                }}
                label={t('Private Activities')}
                {...verticalTabProps(PRIVATE_ACTIVITIES_STATISTICS_TAB)}
              />
            </Tabs>
          </div>
          <div className={classes.datePickerWrapper}>
            <Field
              name="statisticsPeriod"
              inputClassName={classes.inputClassName}
              className={classes.datePickerWrapper}
              minimumNights={0}
              availableRange={{
                endDate: moment(),
                startDate: moment('1970-01-01').format('YYYY-MM-DD'),
                daysOfWeek: []
              }}
              isOutsideRange={() => false}
              component={CustomDateRangePicker}
              statistics
            />
          </div>
        </div>
      )}
      {businessOwner.selectedBusinessOwner.id && (
        <div className={classes.content}>
          <div className={classes.tabsRoot}>
            <TabPanel
              className={classes.tabContentWrapper}
              value={value}
              index={ACCOMMODATION_STATISTICS_TAB}
            >
              {statisticsData && statisticsData.accommodationStatistics && (
                <StatisticsInfoTab
                  total={statisticsData.accommodationStatistics.total}
                  description="Slots are booked"
                  helpText="Number of slots is the number of days when the accommodations are booked"
                  overallLegendData={
                    statisticsData.accommodationStatistics.overallLegendData
                  }
                  platformsLegendData={
                    statisticsData.accommodationStatistics.platformsLegendData
                  }
                />
              )}
            </TabPanel>
            <TabPanel
              className={classes.tabContentWrapper}
              value={value}
              index={OPEN_ACTIVITIES_STATISTICS_TAB}
            >
              {statisticsData && statisticsData.openActivitiesStatistics && (
                <StatisticsInfoTab
                  total={statisticsData.openActivitiesStatistics.total}
                  description="Participants who booked activities in total"
                  helpText="Number of participants who booked activities within the selected period"
                  overallLegendData={
                    statisticsData.openActivitiesStatistics.overallLegendData
                  }
                  platformsLegendData={
                    statisticsData.openActivitiesStatistics.platformsLegendData
                  }
                />
              )}
            </TabPanel>
            <TabPanel
              className={classes.tabContentWrapper}
              value={value}
              index={PRIVATE_ACTIVITIES_STATISTICS_TAB}
            >
              {statisticsData && statisticsData.privateActivitiesStatistics && (
                <StatisticsInfoTab
                  total={statisticsData.privateActivitiesStatistics.total}
                  description="Slots are booked"
                  helpText="Number of slots is the number of each available date and / or start time when the activity is active and booked"
                  overallLegendData={
                    statisticsData.privateActivitiesStatistics.overallLegendData
                  }
                  platformsLegendData={
                    statisticsData.privateActivitiesStatistics
                      .platformsLegendData
                  }
                />
              )}
            </TabPanel>
          </div>
          {value === ACCOMMODATION_STATISTICS_TAB &&
            statisticsData.accommodationStatistics && (
              <StatisticsBookingsList
                bookingsData={
                  statisticsData.accommodationStatistics.bookingsInfo
                }
                statisticsType="accommodationStatistics"
                tableTitle="Accommodations list"
              />
            )}
          {value === OPEN_ACTIVITIES_STATISTICS_TAB &&
            statisticsData.openActivitiesStatistics && (
              <StatisticsBookingsList
                bookingsData={
                  statisticsData.openActivitiesStatistics.bookingsInfo
                }
                statisticsType="openActivitiesStatistics"
                tableTitle="Open Activities list"
              />
            )}
          {value === PRIVATE_ACTIVITIES_STATISTICS_TAB &&
            statisticsData.privateActivitiesStatistics && (
              <StatisticsBookingsList
                bookingsData={
                  statisticsData.privateActivitiesStatistics.bookingsInfo
                }
                statisticsType="privateActivitiesStatistics"
                tableTitle="Private Activities list"
              />
            )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => {
  return {
    ...state.form,
    businessOwner: state.businessOwner
  };
};

export default reduxForm({
  form: 'statisticsDate',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  touchOnBlur: false,
  initialValues
})(connect(mapStateToProps, null)(Statistics));
